/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

// Split out of kHTML.ts 2026-10-19 (owner: smaller units for the reuse reviewer). kHTML.ts
// re-exports every class here, so `import { kDiv } from "./kHTML"` keeps working.
import ks from "../ReUsableTS/kBrowserShared";
import {
    kTime,
    kObject,
    kObjectArray,
    kBoolean,
} from "../ReUsableTS/kBrowserObjects";
import { kFormTheme } from "./kFormTheme";
import { kHTMLElementCore } from "../kReusableCoreTS/kHTMLCore";
import { kProgressBarCore, kCheckBoxCore } from "../kReusableCoreTS/kHTMLWidgetsCore";
import { kHTMLTable } from "./kHTMLTable";
import { kHTMLRow } from "./kHTMLRow";

export class kDateInput {
    // InputType lets one control serve "date" (default — unchanged for every
    // existing caller), "datetime-local" (date + time), and "time". Added as a
    // defaulted parameter so the shared class covers the date-and-time need
    // without a duplicate class.
    constructor(InputType: string = "date") {
        this.Input = document.createElement("input");
        this.Input.type = InputType;
        this.InputType = InputType;
        this.ChosenDate = new kTime();
        // Default value matches the control type: date → today; datetime-local →
        // today + current time (the control needs "yyyy-MM-ddTHH:mm"); time and any
        // other type start empty so the user picks explicitly.
        if (InputType === "date") {
            this.Input.value = this.ChosenDate.ToISODate();
        } else if (InputType === "datetime-local") {
            this.Input.value = this.ChosenDate.ToISODateTime().replace(" ", "T");
        }
        // The old inline `borderColor:"grey"; borderWidth:"thin"` is replaced by the
        // shared .kField class (kFormTheme): radius, hover, focus ring, and - the part
        // that actually made these look dated - the suppressed browser spin button and
        // a toned-down calendar picker glyph. Inline styles would have outranked the
        // sheet, so they have to go rather than sit alongside it.
        kFormTheme.Apply(this.Input, "kField");
        this.Input.addEventListener("change", this._DateChange);
    }
    public Input: HTMLInputElement = document.createElement("input");
    public InputType = "date";
    public ParentID = "";
    public ParentElement: Element | null = null;
    public ID = "";
    public ChosenDate: kTime | null = null;
    public ChangeCB: any | null = null;
    public Create = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Input) {
                this.Input.id = this.ID;
                if (null !== this.ParentElement) {
                    this.ParentElement.appendChild(this.Input);
                } else {
                    if (ks.KNotEmptyString(this.ParentID)) {
                        this.ParentElement = document.getElementById(
                            this.ParentID
                        );
                        if (null !== this.ParentElement) {
                            this.ParentElement.appendChild(this.Input);
                        }
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDateInput.Create");
        }
    };
    private _DateChange = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.ChosenDate = new kTime();
            if (null !== this.Input) {
                if (this.InputType === "datetime-local") {
                    this.ChosenDate.FromISODateTime(this.Input.value.replace("T", " "));
                } else {
                    this.ChosenDate.fromISO(this.Input.value);
                }
            }
            ks.DoCallBack(this.ChangeCB, null, false);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDateInput._DateChange");
        }
    };
    public SetBykDate = (NewDate: kTime | null|""): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if(NewDate!==""){
                if (null !== this.Input && null !== NewDate) {
                    this.Input.value = NewDate.ToISODate();
                }
                this.ChosenDate = NewDate;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDateInput.SetBykDate");
        }
    };
}

// Extends the server-safe kHTMLElementCore (generic element builder):
// inherits Id / class / style / children string emission. This browser
// subclass keeps the live `Div` DOM node it has always exposed.
export class kDiv extends kHTMLElementCore {
    public Div: HTMLDivElement = document.createElement("div");
    public constructor(ID?: string) {
        super("div", ID);
        this.Div = document.createElement("div");
        if (ks.KNotNullOrUndefined(ID)) {
            if (null !== this.Div) {
                this.Div.id = ks.Nz(ID);
            }
        }
    }
}

// Extends the server-safe kProgressBarCore: the percentage clamp
// (SetDoneCore) and the static bar HTML (ToHtmlString) live in the base;
// DonePerc is inherited. This browser subclass keeps the clickable DOM
// bar built from a live kHTMLTable.
export class kProgressTable extends kProgressBarCore {
    public Table: kHTMLTable = new kHTMLTable();
    public DoneCell: HTMLTableCellElement = document.createElement("td");
    public ToDoCell: HTMLTableCellElement = document.createElement("td");
    public Parent: HTMLElement | null = null;
    public ChangeCB: any | null = null;
    public Changable = true;
    public Width = 600;
    public CaptionCell: HTMLTableCellElement | null = null;
    /**
     * The two-cell bar itself: a blue "done" cell that animates its width, and the grey
     * remainder. Split out of Create 2026-08-06 for the 40-line limit. It assigns the SHARED
     * DoneCell / ToDoCell — SetDone resizes those references, so they must stay fields.
     */
    private _BuildProgressRow = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Table === null) { return; }
            const ProgressColorRow: kHTMLRow | null = this.Table.AddEmptyRow();
            if (ProgressColorRow === null || ProgressColorRow.Row === null) { return; }
            ProgressColorRow.Row.style.padding = "0px";

            this.DoneCell = ProgressColorRow.AddCell();
            if (null !== this.DoneCell) {
                const DoneStyle: CSSStyleDeclaration = this.DoneCell.style;
                DoneStyle.backgroundColor = "blue";
                DoneStyle.borderRadius = "5px";
                DoneStyle.height = "12px";
                DoneStyle.display = "table-cell";
                DoneStyle.color = "white";
                DoneStyle.textAlign = "center";
                DoneStyle.transitionProperty = "width";
                DoneStyle.transitionDuration = "1s";
            }
            this.ToDoCell = ProgressColorRow.AddCell();
            if (null !== this.ToDoCell) {
                this.ToDoCell.style.height = "12px";
                this.ToDoCell.style.display = "table-cell";
            }
            if (this.Changable && this.DoneCell && this.ToDoCell) {
                this.DoneCell.addEventListener("click", this._DoneClick);
                this.ToDoCell.addEventListener("click", this._TodoClick);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kProgressTable._BuildProgressRow");
        }
    };

    public Create = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.Table = new kHTMLTable();
            if (null !== this.Table.Table) {
                this.Table.Table.style.width = this.Width + "px";
                this.Table.Table.style.cursor = "pointer";
                this.Table.Table.style.borderCollapse = "collapse";
                this.Table.AddBorders();
            }

            this._BuildProgressRow();
            // const CaptionRow: kHTMLRow | null = this.Table.AddEmptyRow();
            // if(CaptionRow){
            //     this.CaptionCell = CaptionRow.AddMergedCell(2);
            // }
            this.SetDone(this.DonePerc);
            if (null !== this.Parent) {
                if (null !== this.Table.Table) {
                    this.Parent.appendChild(this.Table.Table);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kProgressTable.Create");
        }
    };
    /** @description Set the progress bar width.
     * @param {NewPerc} Use an integer between 0 and 100
     */
    public SetDone = (NewPerc: number): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.DonePerc = NewPerc;
            NewPerc = Math.floor(NewPerc);
            if (
                this.DoneCell &&
                this.ToDoCell &&
                this.Table &&
                this.Table.Table
            ) {
                //this.DoneCell.textContent = NewPerc.toString() + "%";
                this.DoneCell.style.width = NewPerc + "%";
                if (NewPerc !== 100) {
                    this.ToDoCell.style.width = 100 - NewPerc + "%";
                    this.ToDoCell.style.display = "table-cell";
                } else {
                    this.ToDoCell.style.display = "none";
                }
                this.Table.Table.title = NewPerc.toString() + "%";
                this.DoneCell.textContent = NewPerc.toString() + "%";
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kProgressTable.SetDone");
        }
    };
    public SetTooltip = (TooltipText: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.Table.Table.title = TooltipText;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kProgressTable.SetTooltip");
        }
    };
    private _DoneClick = (Event: MouseEvent): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Table && this.Table.Table && this.DoneCell) {
                const TotalWidth: number = this.Table.Table.clientWidth;
                const ClickLeft = Event.offsetX;
                let NewPerc: number = (ClickLeft / TotalWidth) * 100;
                NewPerc = Math.round(NewPerc / 10) * 10;
                this.SetDone(NewPerc);
                ks.DoCallBack(this.ChangeCB, this.DonePerc, false);
                Event.preventDefault();
                Event.stopPropagation();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kProgressTable._DoneClick");
        }
    };
    private _TodoClick = (Event: MouseEvent): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Table && this.Table.Table && this.ToDoCell) {
                const RemainingPerc: number = 100 - this.DonePerc;
                let NewPerc: number =
                    this.DonePerc +
                    RemainingPerc * (Event.offsetX / this.ToDoCell.clientWidth);
                NewPerc = Math.round(NewPerc / 10) * 10;
                this.SetDone(NewPerc);
                ks.DoCallBack(this.ChangeCB, this.DonePerc, false);
                Event.preventDefault();
                Event.stopPropagation();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kProgressTable._TodoClick");
        }
    };
}

export class kCheckboxTable {
    public Table: kHTMLTable | null = null;
    public CurrentArray: kObjectArray | null = null;
    public ParentID = "";
    public CheckBoxes: kCheckBoxForUncheck[] = [];
    // eslint-disable-next-line @typescript-eslint/ban-types
    public ArrayChangedCB: Function | null = null;
    public MultiChoice = true;
    public constructor() {
        this.Table = new kHTMLTable();
    }

    public FromArray = (ParentID: string, ArrayToShow: kObjectArray): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.CurrentArray = ArrayToShow;
            this.ParentID = ParentID;
            this.Render();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCheckboxTable.FromArray");
        }
    };
    public ItemClicked = (UncheckedObject: kObject): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.CurrentArray) {
                this.CurrentArray.RemoveItemByUniqueID(
                    UncheckedObject.UniqueID
                );
                this.Render();
            }
            ks.DoCallBack(this.ArrayChangedCB, null, false);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCheckboxTable.ItemClicked");
        }
    };
    /**
     * Single-choice mode: collapse the array to the LAST item picked. Split out of Render
     * 2026-08-06 for the 40-line limit. It MUTATES CurrentArray in place (pop-then-refill)
     * rather than reassigning, because callers hold the same kObjectArray reference.
     */
    private _KeepOnlyLatestWhenSingleChoice = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.MultiChoice !== false) { return; }
            if (this.CurrentArray === null) { return; }
            if (this.CurrentArray.Items.length <= 1) { return; }
            const LatestItem: kObject | undefined = this.CurrentArray.Items.pop();
            this.CurrentArray.Items = [];
            if (undefined !== LatestItem) {
                this.CurrentArray.Items.push(LatestItem);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCheckboxTable._KeepOnlyLatestWhenSingleChoice");
        }
    };

    public Render = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let CurrObject: kObject | null = null;
            let CurrRow: kHTMLRow | null = null;
            let CurrCheck: kCheckBoxForUncheck | null = null;
            const ParentElement: HTMLElement | null = document.getElementById(this.ParentID);
            if (ParentElement !== null) {
                ParentElement.innerHTML = "";
            }
            this.Table = new kHTMLTable();
            this._KeepOnlyLatestWhenSingleChoice();
            if (null !== this.CurrentArray) {
                for (CurrObject of this.CurrentArray.Items) {
                    CurrRow = this.Table.AddEmptyRow();
                    CurrCheck = new kCheckBoxForUncheck();
                    CurrCheck.ClickCB = this.ItemClicked;
                    if (null !== CurrRow) {
                        CurrCheck.AddToElement(CurrRow.Row, CurrObject);
                    }
                }
            }
            const ParentEl: HTMLElement | null = document.getElementById(
                this.ParentID
            );
            if (null !== ParentEl) {
                if (null !== this.Table.Table) {
                    ParentEl.appendChild(this.Table.Table);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCheckboxTable.Render");
        }
    };
}

export class kCheckBoxForUncheck {
    // eslint-disable-next-line @typescript-eslint/ban-types
    public ClickCB: Function | null = null;
    public CheckBox: HTMLInputElement | null = null;
    public ParentElement: Element | null = null;
    public RelatedObject: kObject | null = null;

    public AddToElement = (
        ParentElement: Element | null,
        ObjectToShow: kObject
    ): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const ContainerDiv: HTMLDivElement | null = new kDiv().Div;
            this.ParentElement = ParentElement;
            this.RelatedObject = ObjectToShow;

            this.CheckBox = document.createElement("input");
            this.CheckBox.type = "checkbox";
            this.CheckBox.checked = true;
            this.CheckBox.addEventListener("change", this._CheckboxClick);
            if (null !== ContainerDiv) {
                ContainerDiv.appendChild(this.CheckBox);
                const Link: HTMLAnchorElement | null = ObjectToShow.ToAnchor();
                if (null !== Link) {
                    ContainerDiv.appendChild(Link);
                }
                if (null !== this.ParentElement) {
                    this.ParentElement.appendChild(ContainerDiv);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCheckBoxForUncheck.AddToElement");
        }
    };
    private _CheckboxClick = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            ks.DoCallBack(this.ClickCB, this.RelatedObject);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kCheckBoxForUncheck._CheckboxClick");
        }
    };
}

// Extends the server-safe kCheckBoxCore: the checked state (Checked /
// SetCheckedCore) and the static disabled-checkbox HTML (ToHtmlString)
// live in the base. This browser subclass keeps the live interactive
// input + the SharePoint reset fix.
export class kHTMLCheckBox extends kCheckBoxCore {
    public CheckboxValue = false;
    public ClickCB: any | null = null;
    public CheckBox: HTMLInputElement = document.createElement("input");
    public Label:HTMLLabelElement = document.createElement("label");
    public constructor(ID?: string) {
        super(ID);
        this.CheckBox = document.createElement("input");
        this.CheckBox.type = "checkbox";
        // .kCheck sizes it to 16px and accent-colours it. The browser default is 13px
        // and system-blue, which reads small and off-palette next to 32px fields.
        kFormTheme.Apply(this.CheckBox, "kCheck");
        this.CheckBox.addEventListener("click", this._CheckboxClick);
        if (ks.KNotNullOrUndefined(ID)) {
            this.CheckBox.id = ks.Nz(ID);
        }else{
            this.CheckBox.id = "kHTMLCheckBox" +  Math.floor(Math.random() * 1000);
        }
        this.Label.htmlFor = this.CheckBox.id;
        this.Label.style.verticalAlign = "top";
    }
    public FromkBool = (YesNo: kBoolean): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.SetCheckedStatus(YesNo.Value);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLCheckBox.FromkBool");
        }
    };
    // eslint-disable-next-line @typescript-eslint/ban-types



    private _CheckboxClick = (event:MouseEvent): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            event.preventDefault();
            if (this.CheckBox) {
                this.CheckboxValue = this.CheckBox.checked;
            }
            ks.DoCallBack(this.ClickCB, undefined, false);
            ks.AddDelayedAction(
                "Checkbox reset SP fix",
                this._ResetValueInSP,
                "If not the SP built in code will mess with the checkbox",
                100
            );
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLCheckBox._CheckboxClick");
            return;
        }
    };
    private _ResetValueInSP = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.CheckBox) {
                this.CheckBox.checked = this.CheckboxValue;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLCheckBox._ResetValueInSP");
        }
    };
    public Check = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.CheckBox.checked = true;
            this.CheckboxValue = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLCheckBox.Check");
            return;
        }
    };

    public SetCheckedStatus = (NewValue: boolean): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.CheckBox) {
                this.CheckBox.checked = NewValue;
                this.CheckboxValue = NewValue;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLCheckBox.SetCheckedStatus");
            return;
        }
    };
}
